import {
  EntityLinks,
  Goal,
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  ID,
  LifeArea,
  RelationshipUpdate,
  UpdateGoal,
} from '@griply/types';
import {
  buildParentToChildrenMap,
  getDirectGoalLifeAreaIds,
  getGoalParentIds,
  getGoalSubtreeIds,
  resolveFrozenGoalColor,
} from '@griply/utils';

import { buildGoalLinkWrites } from './build-goal-link-writes';
import { buildGoalParentLinkWrites } from './build-goal-parent-link-writes';

export type MergeGoalLinkChangesOptions = {
  updatedGoals: UpdateGoal[];
  goalParentChanges?: GoalParentLinkChange[];
  goalLifeAreaChanges?: GoalLifeAreaLinkChange[];
  goals: Goal[];
  links: EntityLinks;
  // Without them a life-area-inheriting goal freezes nothing and stays colourless once migrated.
  lifeAreas?: LifeArea[];
};

export type MergedGoalLinkChanges = {
  goalUpdates: UpdateGoal[];
  relationshipUpdates: RelationshipUpdate[];
};

// Folds link changes into the goal updates the caller already writes — the rules reject a 2nd write to a document within 100ms.
export const mergeGoalLinkChanges = ({
  updatedGoals,
  goalParentChanges = [],
  goalLifeAreaChanges = [],
  goals,
  links,
  lifeAreas = [],
}: MergeGoalLinkChangesOptions): MergedGoalLinkChanges => {
  if (!goalParentChanges.length && !goalLifeAreaChanges.length) {
    return { goalUpdates: updatedGoals, relationshipUpdates: [] };
  }

  const goalsById = new Map<ID, Goal>(goals.map((goal) => [goal.id, goal]));
  const relationshipUpdates: RelationshipUpdate[] = [];
  const linkUpdatesById = new Map<ID, UpdateGoal>();
  const reparentedIds = new Set<ID>();

  // Resolved against the pre-move links, and once per goal even when both link kinds move.
  const frozenColorById = new Map<ID, string | undefined>();
  const frozenColorFor = (goal: Goal): string | undefined => {
    if (frozenColorById.has(goal.id)) {
      return frozenColorById.get(goal.id);
    }
    const color = resolveFrozenGoalColor(goal, { goals, lifeAreas, links });
    frozenColorById.set(goal.id, color);
    return color;
  };

  const mergeLinkUpdate = (update: UpdateGoal | null) => {
    if (!update) {
      return;
    }
    linkUpdatesById.set(update.id, {
      ...linkUpdatesById.get(update.id),
      ...update,
    });
  };

  goalLifeAreaChanges.forEach(({ goalId, lifeAreaIds }) => {
    const goal = goalsById.get(goalId);
    if (!goal) {
      return;
    }
    const writes = buildGoalLinkWrites({
      goalId,
      lifeAreaIds,
      currentLifeAreaIds: getDirectGoalLifeAreaIds(goal, links),
      isMigrated: !!goal.lifeAreaLinksMigrated,
      frozenColor: goal.lifeAreaLinksMigrated
        ? undefined
        : frozenColorFor(goal),
    });
    mergeLinkUpdate(writes.goalUpdate);
    relationshipUpdates.push(...writes.relationshipUpdates);
  });

  const parentToChildrenMap = buildParentToChildrenMap(goals, links);
  goalParentChanges.forEach(({ goalId, parentIds }) => {
    const goal = goalsById.get(goalId);
    if (!goal) {
      return;
    }
    const writes = buildGoalParentLinkWrites({
      goalId,
      parentIds,
      currentParentIds: getGoalParentIds(goal, { links }),
      subtreeIds: getGoalSubtreeIds(goalId, {
        goals,
        links,
        parentToChildrenMap,
      }),
      isMigrated: !!goal.parentLinksMigrated,
      frozenColor: goal.parentLinksMigrated ? undefined : frozenColorFor(goal),
    });
    if (writes.relationshipUpdates.length) {
      reparentedIds.add(goalId);
    }
    mergeLinkUpdate(writes.goalUpdate);
    relationshipUpdates.push(...writes.relationshipUpdates);
  });

  // A re-parent moves one link; the frozen legacy chain must not be rewritten alongside it.
  const merged = updatedGoals.map(({ ancestorIds, ...goal }) => ({
    ...goal,
    ...(reparentedIds.has(goal.id) || ancestorIds === undefined
      ? {}
      : { ancestorIds }),
    ...linkUpdatesById.get(goal.id),
  }));
  const mergedIds = new Set(merged.map(({ id }) => id));

  return {
    goalUpdates: [
      ...merged,
      ...Array.from(linkUpdatesById.values()).filter(
        ({ id }) => !mergedIds.has(id),
      ),
    ],
    relationshipUpdates,
  };
};
